import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import api from './api';

const STORAGE_KEY = 'cashflow_user_id';

const CashFlowContext = createContext(null);

export function CashFlowProvider({ children }) {
  const [currentUserId, setCurrentUserIdState] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? Number(stored) : null;
  });
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);

  const now = dayjs();
  const [yearMonth, setYearMonth] = useState({ year: now.year(), month: now.month() + 1 });

  const refreshUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      const { data } = await api.get('/users');
      setUsers(data);
    } finally {
      setUsersLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshUsers();
  }, [refreshUsers]);

  const setCurrentUserId = useCallback((id) => {
    if (id == null) {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, String(id));
    }
    setCurrentUserIdState(id);
  }, []);

  const value = useMemo(() => {
    const currentUser = users.find((u) => u.id === currentUserId) || null;
    const otherUser = users.find((u) => u.id !== currentUserId) || null;
    return {
      users,
      usersLoading,
      refreshUsers,
      currentUserId,
      currentUser,
      otherUser,
      setCurrentUserId,
      yearMonth,
      setYearMonth,
    };
  }, [users, usersLoading, refreshUsers, currentUserId, setCurrentUserId, yearMonth]);

  return <CashFlowContext.Provider value={value}>{children}</CashFlowContext.Provider>;
}

export function useCashFlow() {
  const ctx = useContext(CashFlowContext);
  if (!ctx) throw new Error('useCashFlow must be used within CashFlowProvider');
  return ctx;
}
